import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Form, notification, Spin } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { useHistory } from "react-router-dom";
import { PiWarningCircleFill } from "react-icons/pi";

import {
  StyledButton,
  StyledModal,
  StyledFooterButton,
  StyledCancelButton,
  StyledModalTitle,
  StyledModalTitleContainer,
  StyledCheckCircle,
  ModalContent,
  EmptySubCategoriesMessage,
  SubCategoryField,
  FieldLabel,
  TagsContainer,
  SubCategoryTag,
} from "./styles";
import api from "../../service";
import { timerManager } from "../../utils/timerManager";
import { resetSaleInfo } from "../../redux/modules/staff-acess/actions";
import Select from "../../newUXComponents/Select";

const FETCH_PARAMS = { page: 0, size: 100, sortBy: "id" };

const EndServiceButton = ({ header = false }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const history = useHistory();
  const { token } = useSelector((state) => state.auth);
  const [form] = Form.useForm();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoadingSubCategories, setIsLoadingSubCategories] = useState(false);
  const [selectedSubCategoryIds, setSelectedSubCategoryIds] = useState([]);

  const serviceId = localStorage.getItem("serviceId");
  const contractNumber = localStorage.getItem("serviceContractNumber");
  const systemUserId = localStorage.getItem("serviceSystemUserId");

  const authHeaders = useMemo(
    () => ({
      "Content-Type": "application/json",
      Accept: "*/*",
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const categoriesOptions = useMemo(
    () => categories.map(({ id, title }) => ({ value: id, label: title })),
    [categories]
  );

  const hasNoSubCategories = useMemo(
    () =>
      Boolean(selectedCategory) &&
      !isLoadingSubCategories &&
      subCategories.length === 0,
    [selectedCategory, isLoadingSubCategories, subCategories.length]
  );

  const isFinishDisabled =
    !selectedCategory || hasNoSubCategories || !selectedSubCategoryIds.length;

  const toggleSubCategory = useCallback((id) => {
    setSelectedSubCategoryIds((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id]
    );
  }, []);

  const handleError = useCallback(
    (error, key = "description") => {
      notification.error({
        message: intl.formatMessage({ id: "error" }),
        description: intl.formatMessage({
          id: `api.error.${error.response?.data?.[key]}`,
        }),
      });
    },
    [intl]
  );

  const fetchCategories = useCallback(async () => {
    try {
      const { data } = await api.get("/service-categories", {
        headers: authHeaders,
        params: FETCH_PARAMS,
      });
      setCategories(data.categoryOutPutList);
    } catch (error) {
      handleError(error);
    }
  }, [authHeaders, handleError]);

  const fetchSubCategories = useCallback(
    async (categoryId) => {
      setIsLoadingSubCategories(true);
      try {
        const { data } = await api.get(
          `/service-sub-categories/category/${categoryId}`,
          { headers: authHeaders, params: FETCH_PARAMS }
        );
        const list = data?.subCategoryOutPutLists ?? [];
        setSubCategories(list);

        // Feedback imediato quando não há subcategorias cadastradas
      } catch (error) {
        handleError(error);
      } finally {
        setIsLoadingSubCategories(false);
      }
    },
    [authHeaders, handleError]
  );

  const handleCategoryChange = useCallback(
    (value) => {
      const selected = categories.find(({ id }) => id === value);
      setSelectedCategory(selected);
      setSubCategories([]);
      setSelectedSubCategoryIds([]);
      fetchSubCategories(value);
    },
    [categories, fetchSubCategories]
  );

  const handleCloseModal = useCallback(() => {
    setIsModalVisible(false);
    setSelectedCategory(null);
    setSubCategories([]);
    form.resetFields();
  }, [form]);

  const finishService = async () => {
    try {
      const subcategoryParams = selectedSubCategoryIds
        .map((id) => `subcategoryIds=${id}`)
        .join("&");

      await api.put(
        `/staff/attendance/${serviceId}?contract=${contractNumber}&userId=${systemUserId}&categoryId=${selectedCategory?.id}&${subcategoryParams}`,
        {},
        { headers: authHeaders }
      );
    } catch (error) {
      handleError(error, "code");
    } finally {
      setIsModalVisible(false);
      timerManager.reset();
      history.push("/staff-access/select-client");
      dispatch(resetSaleInfo());
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return (
    <>
      <StyledModal
        centered
        onCancel={handleCloseModal}
        closable
        maskClosable
        title={
          <StyledModalTitleContainer>
            <div className="circle">
              <StyledCheckCircle />
            </div>
            <StyledModalTitle>
              {intl.formatMessage({ id: "end_service" })}
            </StyledModalTitle>
          </StyledModalTitleContainer>
        }
        visible={isModalVisible}
        footer={[
          <StyledCancelButton key="cancel" onClick={handleCloseModal}>
            {intl.formatMessage({ id: "cancel" })}
          </StyledCancelButton>,
          <StyledFooterButton
            key="submit"
            type="primary"
            onClick={finishService}
            disabled={isFinishDisabled}
          >
            {intl.formatMessage({ id: "finish" })}
          </StyledFooterButton>,
        ]}
      >
        <ModalContent>
          <Form form={form} layout="vertical">
            <Select
              isStaffLayout
              name="category"
              label={intl.formatMessage({ id: "category" })}
              options={categoriesOptions}
              onChange={handleCategoryChange}
              placeholder={intl.formatMessage({ id: "select" })}
            />
            {selectedCategory && (
              <SubCategoryField>
                <FieldLabel>
                  {intl.formatMessage({ id: "subcategory" })}
                </FieldLabel>

                {isLoadingSubCategories ? (
                  <Spin size="small" />
                ) : hasNoSubCategories ? (
                  <EmptySubCategoriesMessage role="alert">
                    <PiWarningCircleFill size={18} />
                    <span>
                      {intl.formatMessage({ id: "no_subcategories_message" })}
                    </span>
                  </EmptySubCategoriesMessage>
                ) : (
                  <TagsContainer>
                    {subCategories.map(({ id, title }) => (
                      <SubCategoryTag
                        key={id}
                        type="button"
                        $selected={selectedSubCategoryIds.includes(id)}
                        aria-pressed={selectedSubCategoryIds.includes(id)}
                        onClick={() => toggleSubCategory(id)}
                      >
                        {title}
                      </SubCategoryTag>
                    ))}
                  </TagsContainer>
                )}
              </SubCategoryField>
            )}
          </Form>
        </ModalContent>
      </StyledModal>
      <StyledButton onClick={() => setIsModalVisible(true)} header={header}>
        {intl.formatMessage({ id: "end_service" })}
      </StyledButton>
    </>
  );
};

export default EndServiceButton;
